import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, DownIcon, IconButtonWrapper, UpIcon } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, ETimePeriod, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { getBudgetProgressColor, RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartNotes } from '../ChartNotes';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
            padding: '0 15px',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px',
            '& .title': {
                fontSize: 16,
            },
            '& .colWrapper': {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: 15,
                flexFlow: 'row wrap',
            },
            '& .col': {
                fontSize: '16px',
                fontWeight: 600,
                textAlign: 'center',
                border: '1px solid',
                padding: '15px',
                display: 'flex',
                margin: '0px 10px 10px 10px',
                flexFlow: 'column',
                flex: 1,
                '& .value': {
                    fontSize: '36px',
                    padding: '20px 0',
                },
                '& .sub': {
                    display: 'flex',
                    flexFlow: 'row wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                },
                '& .status': {
                    marginLeft: '10px',
                    display: 'flex',
                    flexFlow: 'row wrap',
                    alignItems: 'center',
                },
            },
            '& .increase': {
                color: 'green',
            },
            '& .decrease': {
                color: 'red',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 10,
        },
        span: {
            fontSize: '1rem',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);

export enum OptionsEnum {
    LAST_WEEK,
    LAST_14_DAYS,
    LAST_30_DAYS,
    LAST_12_MOTNHS,
}

interface FeesCollectedVsBudgetByPeriodPartChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    options: OptionsEnum;
    months: string[];
    counts: number[];
    budgetCounts: number[];
    periodString: string;
    currentYearTotal: number;
    currentTotal: number;

    budgetCurrentTotal: number;
    varianceCurrentTotal: number;
    percentageCurrentTotal: number;

    budgetYearTotal: number;
    varianceYearTotal: number;
    percentageYearTotal: number;
    symbol: string;
}

export const FeesCollectedVsBudgetByPeriodPartChart: React.FC<FeesCollectedVsBudgetByPeriodPartChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const feesCollectedVsBudgetByPeriod = useQuery<FeesCollectedVsBudgetByPeriodQueryData>(FeesCollectedVsBudgetByPeriodQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        options: OptionsEnum.LAST_WEEK,
        months: [],
        counts: [],
        budgetCounts: [],
        periodString: 'Week',
        currentYearTotal: 0,
        currentTotal: 0,
        budgetCurrentTotal: 0,
        varianceCurrentTotal: 0,
        percentageCurrentTotal: 0,
        budgetYearTotal: 0,
        varianceYearTotal: 0,
        percentageYearTotal: 0,
        symbol: '$'
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let counts: number[] = [];
            let budgetCounts: number[] = [];
            let periodString: string = 'Week';
            let currentTotal: number = 0;
            let currentYearTotal: number = 0;

            let budgetCurrentTotal = 0;
            let varianceCurrentTotal = 0;
            let percentageCurrentTotal = 0;
            let budgetYearTotal = 0;
            let varianceYearTotal = 0;
            let percentageYearTotal = 0;

            if (!feesCollectedVsBudgetByPeriod.error && feesCollectedVsBudgetByPeriod.data) {
                let lastWeek = feesCollectedVsBudgetByPeriod.data.statistics.fees.feesCollectedVsBudgetByPeriod.lastWeek;
                let last14Days = feesCollectedVsBudgetByPeriod.data.statistics.fees.feesCollectedVsBudgetByPeriod.last14Days;
                let last30Days = feesCollectedVsBudgetByPeriod.data.statistics.fees.feesCollectedVsBudgetByPeriod.last30Days;
                let last12Months = feesCollectedVsBudgetByPeriod.data.statistics.fees.feesCollectedVsBudgetByPeriod.last12Months;

                currentYearTotal = last12Months.summary.lastPeriodTotal;

                budgetYearTotal = last12Months.summary.budget;
                varianceYearTotal = last12Months.summary.variance;
                percentageYearTotal = Math.round(last12Months.summary.percentage);

                if (state.options === OptionsEnum.LAST_WEEK) {
                    months = lastWeek.feesCollectedGrouping.map((s) => s.group);
                    counts = lastWeek.feesCollectedGrouping.map((s) => s.value);
                    budgetCounts = lastWeek.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = lastWeek.summary.lastPeriodTotal;

                    budgetCurrentTotal = lastWeek.summary.budget;
                    varianceCurrentTotal = lastWeek.summary.variance;
                    percentageCurrentTotal = Math.round(lastWeek.summary.percentage);

                    periodString = 'Week';
                } else if (state.options === OptionsEnum.LAST_14_DAYS) {
                    months = last14Days.feesCollectedGrouping.map((s) => s.group);
                    counts = last14Days.feesCollectedGrouping.map((s) => s.value);
                    budgetCounts = last14Days.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = last14Days.summary.lastPeriodTotal;

                    budgetCurrentTotal = last14Days.summary.budget;
                    varianceCurrentTotal = last14Days.summary.variance;
                    percentageCurrentTotal = Math.round(last14Days.summary.percentage);

                    periodString = '14 Days';
                } else if (state.options === OptionsEnum.LAST_30_DAYS) {
                    months = last30Days.feesCollectedGrouping.map((s) => s.group);
                    counts = last30Days.feesCollectedGrouping.map((s) => s.value);
                    budgetCounts = last30Days.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = last30Days.summary.lastPeriodTotal;

                    budgetCurrentTotal = last30Days.summary.budget;
                    varianceCurrentTotal = last30Days.summary.variance;
                    percentageCurrentTotal = Math.round(last30Days.summary.percentage);

                    periodString = '30 Days';
                } else {
                    months = last12Months.feesCollectedGrouping.map((s) => s.group);
                    counts = last12Months.feesCollectedGrouping.map((s) => s.value);
                    budgetCounts = last12Months.feesBudgetGrouping.map((s) => s.value);

                    currentTotal = last12Months.summary.lastPeriodTotal;

                    budgetCurrentTotal = last12Months.summary.budget;
                    varianceCurrentTotal = last12Months.summary.variance;
                    percentageCurrentTotal = Math.round(last12Months.summary.percentage);

                    periodString = '12 Months';
                }
            }

            return {
                ...prevState,
                counts,
                budgetCounts,
                months,
                periodString,
                currentTotal,
                currentYearTotal,
                budgetCurrentTotal,
                varianceCurrentTotal,
                percentageCurrentTotal,
                budgetYearTotal,
                varianceYearTotal,
                percentageYearTotal,
            };
        });
    // tslint:disable-next-line: align
    }, [state.options, feesCollectedVsBudgetByPeriod.loading, feesCollectedVsBudgetByPeriod.error, feesCollectedVsBudgetByPeriod.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isFeesCollectedVsBudgetByPeriodPartChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                options: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isMonth: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let timePeriod = ETimePeriod.THIS_WEEK;

            let defaultView = 'Fees Collected MTD/YTD';
            
            if (isMonth) {
                if (state.options === OptionsEnum.LAST_WEEK) {
                    // This Week
                    searchCriterias.push({ property: 'ThisWeek', operator: 'eq', comparison: 'curr' });
                    timePeriod = ETimePeriod.THIS_WEEK;
                } else if (state.options === OptionsEnum.LAST_14_DAYS) {
                    searchCriterias.push({ property: 'PeriodInDays', operator: 'eq', comparison: 'curr', period: '14' });
                    timePeriod = ETimePeriod.LAST_14_DAYS;
                } else if (state.options === OptionsEnum.LAST_30_DAYS) {
                    searchCriterias.push({ property: 'PeriodInDays', operator: 'eq', comparison: 'curr', period: '30' });
                    timePeriod = ETimePeriod.LAST_30_DAYS;
                } else {
                    searchCriterias.push({ property: '12MonthPeriod', operator: 'eq', comparison: 'curr' });
                    timePeriod = ETimePeriod.LAST_12_MONTHS;
                }
            } else {
                searchCriterias.push({ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' });
                timePeriod = ETimePeriod.LAST_12_MONTHS;
            }

            const title = `Drilldown from Fees Collected vs Budget By Period for ${timePeriod}`;

            props.getSelectedItem(searchCriterias, false, title, DrillDownType.staffBillingSummary, defaultView);
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (currencySymbolQuery.loading || feesCollectedVsBudgetByPeriod.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.budgetCounts, state.counts) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={props.showAllRecords ? 'large' : 'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MonetizationOnOutlinedIcon />} title="Fees Collected vs Budget by Period" />
                <div className="colWrapper">
                    <div className="col">
                        <div className="title">
                            {state.periodString === 'Week' ? 'This ' : 'Last '} {state.periodString}
                        </div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(true)}>
                            <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageCurrentTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageCurrentTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(true)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetCurrentTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(true)}>
                                {`${formatNumber(state.percentageCurrentTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetCurrentTotal < state.currentTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(true)} title="Variance">
                                {state.budgetCurrentTotal < state.currentTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.varianceCurrentTotal)}`}
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="title">Last 12 Months</div>
                        <div className={`${classes.totalDiv} value`} onClick={() => handleTotalClick(false)}>
                            <AnimatedNumber value={state.currentYearTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        </div>
                        <div className="rv-meter">
                            <i style={{ width: `${Math.min(100, Math.max(0, state.percentageYearTotal))}%`, backgroundColor: getBudgetProgressColor(state.percentageYearTotal) }} />
                        </div>
                        <div className={`${classes.totalDiv} rv-budget`} onClick={() => handleTotalClick(false)}>
                            {`Budget ${state.symbol}${formatNumber(state.budgetYearTotal)}`}
                        </div>
                        <div className="rv-stats">
                            <div className={`${classes.totalDiv} rv-pct`} onClick={() => handleTotalClick(false)}>
                                {`${formatNumber(state.percentageYearTotal)}% achvd`}
                            </div>
                            <div className={`rv-variance ${state.budgetYearTotal < state.currentYearTotal ? 'good' : 'bad'}`} onClick={() => handleTotalClick(false)} title="Variance">
                                {state.budgetYearTotal < state.currentYearTotal ? <UpIcon className="increase" /> : <DownIcon className="decrease" />}
                                {`${state.symbol}${formatNumber(state.varianceYearTotal)}`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            // const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            // const item = state.months[_index];

                            // const searchCriterias = new Array<SearchCriteria>();

                            // if (state.options === OptionsEnum.LAST_WEEK ||
                            //     state.options === OptionsEnum.LAST_14_DAYS ||
                            //     state.options === OptionsEnum.LAST_30_DAYS) {
                            //         searchCriterias.push(
                            //             { property: 'DateShortString', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May 05
                            //         );
                            // } else {
                            //     searchCriterias.push(
                            //         { property: 'Month', operator: 'eq', comparison: item, period: 'curr' }, // e.g. May
                            //     );
                            // }

                            // let defaultView = 'Fees Collected MTD/YTD';

                            // if (state.options === OptionsEnum.LAST_WEEK ) {
                            //     defaultView = 'Fees Collected MTD/YTD';
                            // }

                            // props.getSelectedItem(
                            //     searchCriterias,
                            //     false,
                            //     'Fees Collected - ' + item,
                            //     DrillDownType.staffBillingSummary,
                            //     defaultView
                            // );
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                label: 'Budget',
                                fill: false,
                                backgroundColor: RV_CHART_COLORS.amber,
                                borderColor: RV_CHART_COLORS.amber,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.amber,
                                hoverBorderColor: RV_CHART_COLORS.amber,
                                tension: 0.35,
                                pointRadius: 0,
                                data: state.budgetCounts,
                                type: 'line',
                                pointStyle: 'line',
                            },
                            {
                                fill: false,
                                label: 'Fees Collected',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.counts,
                                type: 'bar',
                            },
                        // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.budgetCounts, ...state.counts]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.budgetCounts, ...state.counts]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.budgetCounts, state.counts) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={OptionsEnum.LAST_WEEK} control={<Radio color="primary" />} label="This Week" />
                    <FormControlLabel value={OptionsEnum.LAST_14_DAYS} control={<Radio color="primary" />} label="Last 14 days" />
                    <FormControlLabel value={OptionsEnum.LAST_30_DAYS} control={<Radio color="primary" />} label="Last 30 days" />
                    <FormControlLabel value={OptionsEnum.LAST_12_MOTNHS} control={<Radio color="primary" />} label="Last 12 months" />
                </RadioGroup>
            </FormControl>
            <ChartNotes notes={['Information collated based on Timekeeper']} actionHint="Drill down to timekeeper details" />
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;
export interface FeesCollectedVsBudgetByPeriodQueryParams {
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
}
export interface FeesCollectedVsBudgetByPeriodQueryData {
    statistics: Statistics;
}

export interface Statistics {
    fees: Fees;
}

export interface Fees {
    feesCollectedVsBudgetByPeriod: FeesCollectedVsBudgetByPeriod;
}

export interface FeesCollectedVsBudgetByPeriod {
    lastWeek: Last;
    last14Days: Last;
    last30Days: Last;
    last12Months: Last;
}

export interface Last {
    summary: Summary;
    feesCollectedGrouping: FeesCollectedGrouping[];
    feesBudgetGrouping: FeesBudgetGrouping[];
}

export interface FeesCollectedGrouping {
    group: string;
    value: number;
}

export interface FeesBudgetGrouping {
    group: string;
    value: number;
}

export interface Summary {
    lastPeriodTotal: number;
    previousPeriodTotal: number;
    variance: number;
    percentage: number;
    budget: number;
}

const FeesCollectedVsBudgetByPeriodQuery = gql`
    query FeesCollectedVsBudgetByPeriodData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            fees {
                feesCollectedVsBudgetByPeriod(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    lastWeek {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    last14Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    last30Days {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                    last12Months {
                        summary {
                            lastPeriodTotal
                            previousPeriodTotal
                            variance
                            percentage
                            budget
                        }
                        feesCollectedGrouping {
                            group
                            value
                        }
                        feesBudgetGrouping {
                            group
                            value
                        }
                    }
                }
            }
        }
    }
`;
